import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { settingsAPI, labelsAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  User,
  Palette,
  Sparkles,
  Tag,
  Plus,
  Edit,
  Trash2,
  Save,
  Clock,
  DollarSign,
  Receipt
} from 'lucide-react';

const AI_MODELS = [
  { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
];

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b'
];

const SettingsPage = () => {
  const { user, checkAuth } = useAuth();
  
  const [settings, setSettings] = useState(null);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Profile form
  const [profileName, setProfileName] = useState('');
  
  // Settings form
  const [aiModel, setAiModel] = useState('gemini-3-flash-preview');
  const [domainPrompt, setDomainPrompt] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Time tracking settings
  const [endOfDayTime, setEndOfDayTime] = useState('17:00');
  const [breaks, setBreaks] = useState([{ name: 'Lunch', start_time: '12:00', end_time: '12:30' }]);
  const [hourlyRate, setHourlyRate] = useState(0);

  // Quote settings
  const [quoteSettings, setQuoteSettings] = useState({
    business_name: '',
    abn: '',
    business_address: '',
    business_phone: '',
    business_email: '',
    quote_hourly_rate: 0,
    quote_validity_days: 30,
    standard_terms: '',
    payment_instructions: '',
    deposit_percentage: 0,
    deposit_note: '',
    gst_registered: false,
    gst_rate: 10,
    gst_inclusive: false,
  });
  
  // Label form
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState('#3b82f6');
  const [labelAiPrompt, setLabelAiPrompt] = useState('');
  
  // Admin users
  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
    }
  }, [user]);

  useEffect(() => {
    if (settings) {
      setAiModel(settings.ai_model || 'gemini-3-flash-preview');
      setDomainPrompt(settings.domain_prompt || '');
      setShowArchived(settings.show_archived || false);
      setEndOfDayTime(settings.end_of_day_time || '17:00');
      setBreaks(settings.breaks || [{ name: 'Lunch', start_time: '12:00', end_time: '12:30' }]);
      setHourlyRate(settings.hourly_rate || 0);
      setQuoteSettings({
        business_name: settings.business_name || '',
        abn: settings.abn || '',
        business_address: settings.business_address || '',
        business_phone: settings.business_phone || '',
        business_email: settings.business_email || '',
        quote_hourly_rate: settings.quote_hourly_rate || 0,
        quote_validity_days: settings.quote_validity_days || 30,
        standard_terms: settings.standard_terms || '',
        payment_instructions: settings.payment_instructions || '',
        deposit_percentage: settings.deposit_percentage || 0,
        deposit_note: settings.deposit_note || '',
        gst_registered: settings.gst_registered || false,
        gst_rate: settings.gst_rate ?? 10,
        gst_inclusive: settings.gst_inclusive || false,
      });
    }
  }, [settings]);

  const fetchData = async () => {
    try {
      const [settingsRes, labelsRes, adminsRes] = await Promise.all([
        settingsAPI.get(),
        labelsAPI.list(),
        usersAPI.admins()
      ]);
      setSettings(settingsRes.data);
      setLabels(labelsRes.data);
      setAdminUsers(adminsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await usersAPI.updateProfile({ name: profileName });
      toast.success('Profile updated');
      checkAuth();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await settingsAPI.update({
        ai_model: aiModel,
        domain_prompt: domainPrompt,
        show_archived: showArchived
      });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSaveTimeSettings = async () => {
    try {
      await settingsAPI.update({
        end_of_day_time: endOfDayTime,
        breaks: breaks.filter(b => b.name && b.start_time && b.end_time),
        hourly_rate: parseFloat(hourlyRate) || 0,
      });
      toast.success('Time tracking settings saved');
    } catch (error) {
      toast.error('Failed to save time settings');
    }
  };

  const addBreak = () => {
    setBreaks([...breaks, { name: '', start_time: '', end_time: '' }]);
  };

  const updateBreak = (index, field, value) => {
    const updated = [...breaks];
    updated[index] = { ...updated[index], [field]: value };
    setBreaks(updated);
  };

  const removeBreak = (index) => {
    setBreaks(breaks.filter((_, i) => i !== index));
  };

  const handleSaveQuoteSettings = async () => {
    try {
      await settingsAPI.update({
        ...quoteSettings,
        quote_hourly_rate: parseFloat(quoteSettings.quote_hourly_rate) || 0,
        quote_validity_days: parseInt(quoteSettings.quote_validity_days) || 30,
        deposit_percentage: parseFloat(quoteSettings.deposit_percentage) || 0,
        gst_rate: parseFloat(quoteSettings.gst_rate) || 10,
      });
      toast.success('Quote settings saved');
    } catch (error) {
      toast.error('Failed to save quote settings');
    }
  };

  const updateQuoteSetting = (field, value) => {
    setQuoteSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateLabel = () => {
    setEditingLabel(null);
    setLabelName('');
    setLabelColor('#3b82f6');
    setLabelAiPrompt('');
    setShowLabelDialog(true);
  };

  const handleEditLabel = (label) => {
    setEditingLabel(label);
    setLabelName(label.name);
    setLabelColor(label.color);
    setLabelAiPrompt(label.ai_prompt || '');
    setShowLabelDialog(true);
  };

  const handleSaveLabel = async () => {
    if (!labelName.trim()) {
      toast.error('Label name is required');
      return;
    }

    try {
      const data = { name: labelName, color: labelColor, ai_prompt: labelAiPrompt || null };
      if (editingLabel) {
        await labelsAPI.update(editingLabel.label_id, data);
        toast.success('Label updated');
      } else {
        await labelsAPI.create(data);
        toast.success('Label created');
      }
      setShowLabelDialog(false);
      const response = await labelsAPI.list();
      setLabels(response.data);
    } catch (error) {
      toast.error('Failed to save label');
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm('Delete this label? It will be removed from all tasks.')) return;
    
    try {
      await labelsAPI.delete(labelId);
      toast.success('Label deleted');
      const response = await labelsAPI.list();
      setLabels(response.data);
    } catch (error) {
      toast.error('Failed to delete label');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading">Settings</h1>
        <p className="text-slate-600">Manage your preferences and configurations</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="mr-2 h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="labels">
            <Tag className="mr-2 h-4 w-4" />
            Labels
          </TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="mr-2 h-4 w-4" />
            AI Settings
          </TabsTrigger>
          <TabsTrigger value="time" data-testid="settings-time-tab">
            <Clock className="mr-2 h-4 w-4" />
            Time Tracking
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="settings-quotes-tab">
            <Receipt className="mr-2 h-4 w-4" />
            Quotes
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Profile Settings</CardTitle>
              <CardDescription>Manage your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 max-w-md">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your name"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="mt-2 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>
                <div>
                  <Label>Role</Label>
                  <div className="mt-2">
                    <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
                      {user?.role}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveProfile}>
                <Save className="mr-2 h-4 w-4" />
                Save Profile
              </Button>
            </CardContent>
          </Card>
          
          {/* Admin Users */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading">Administrators</CardTitle>
              <CardDescription>Users with admin access to this system</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                readOnly
                value={adminUsers.map(a => `${a.name} — ${a.email}`).join('\n') || 'No admin users found'}
                rows={Math.max(2, adminUsers.length)}
                className="bg-slate-50 font-mono text-sm"
                data-testid="admin-users-list"
              />
            </CardContent>
          </Card>
        </TabsContent>
        {/* Labels Tab */}
        <TabsContent value="labels">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Labels</CardTitle>
                  <CardDescription>Create and manage task labels</CardDescription>
                </div>
                <Button onClick={handleCreateLabel} data-testid="create-label-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Label
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {labels.length === 0 ? (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-600">No labels yet</p>
                  <Button variant="outline" className="mt-4" onClick={handleCreateLabel}>
                    Create your first label
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {labels.map((label) => (
                    <div
                      key={label.label_id}
                      className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div 
                          className="w-6 h-6 rounded-full flex-shrink-0"
                          style={{ backgroundColor: label.color }}
                        />
                        <div className="min-w-0">
                          <span className="font-medium">{label.name}</span>
                          {label.ai_prompt && (
                            <p className="text-xs text-slate-500 truncate">{label.ai_prompt}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditLabel(label)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteLabel(label.label_id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings Tab */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">AI Configuration</CardTitle>
              <CardDescription>Configure AI-powered features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 max-w-2xl">
                <div>
                  <Label>AI Model</Label>
                  <Select value={aiModel} onValueChange={setAiModel}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_MODELS.map((model) => (
                        <SelectItem key={model.value} value={model.value}>
                          {model.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Used for generating suggestions (parts, tools, breakdowns, cautions)
                  </p>
                </div>

                <div>
                  <Label>Domain Prompt</Label>
                  <Textarea
                    value={domainPrompt}
                    onChange={(e) => setDomainPrompt(e.target.value)}
                    placeholder="You are a helpful project management assistant..."
                    rows={4}
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Customize the AI's behavior for your specific domain or industry
                  </p>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Show Archived Tasks</Label>
                    <p className="text-sm text-slate-500">Include archived tasks in default view</p>
                  </div>
                  <Switch
                    checked={showArchived}
                    onCheckedChange={setShowArchived}
                  />
                </div>
              </div>
              
              <Button onClick={handleSaveSettings}>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Tracking Tab */}
        <TabsContent value="time">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Time Tracking Settings</CardTitle>
              <CardDescription>Configure work hours, breaks, and billing rate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 max-w-2xl">
                <div>
                  <Label>End of Work Day</Label>
                  <Input
                    type="time"
                    value={endOfDayTime}
                    onChange={(e) => setEndOfDayTime(e.target.value)}
                    className="mt-2 max-w-xs"
                    data-testid="end-of-day-time"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Open check-ins will be automatically closed at this time
                  </p>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label>Scheduled Breaks</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Break time is deducted from tracked hours</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={addBreak} data-testid="add-break-btn">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Break
                    </Button>
                  </div>
                  {breaks.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No breaks configured</p>
                  ) : (
                    <div className="space-y-3">
                      {breaks.map((b, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`break-row-${idx}`}>
                          <Input
                            placeholder="Name (e.g. Lunch)"
                            value={b.name}
                            onChange={(e) => updateBreak(idx, 'name', e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            type="time"
                            value={b.start_time}
                            onChange={(e) => updateBreak(idx, 'start_time', e.target.value)}
                            className="w-32"
                          />
                          <span className="text-slate-400">to</span>
                          <Input
                            type="time"
                            value={b.end_time}
                            onChange={(e) => updateBreak(idx, 'end_time', e.target.value)}
                            className="w-32"
                          />
                          <Button variant="ghost" size="sm" onClick={() => removeBreak(idx)} className="h-8 w-8 p-0">
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <Label>Hourly Rate (AUD)</Label>
                  <div className="flex items-center gap-2 mt-2 max-w-xs">
                    <span className="text-slate-500 font-medium">$</span>
                    <Input
                      type="number"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      data-testid="hourly-rate-input"
                    />
                    <span className="text-sm text-slate-500 whitespace-nowrap">/ hour</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Used to calculate billing amounts in time reports
                  </p>
                </div>
              </div>

              <Button onClick={handleSaveTimeSettings} data-testid="save-time-settings-btn">
                <Save className="mr-2 h-4 w-4" />
                Save Time Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Quote Settings</CardTitle>
              <CardDescription>Default details applied to new quotes and PDFs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 max-w-2xl">

                {/* Business Details */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Business Details</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Business Name</Label>
                      <Input value={quoteSettings.business_name} onChange={(e) => updateQuoteSetting('business_name', e.target.value)} className="mt-1" placeholder="Your Trade Co." data-testid="qs-business-name" />
                    </div>
                    <div>
                      <Label>ABN</Label>
                      <Input value={quoteSettings.abn} onChange={(e) => updateQuoteSetting('abn', e.target.value)} className="mt-1" placeholder="12 345 678 901" data-testid="qs-abn" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Business Address</Label>
                      <Textarea value={quoteSettings.business_address} onChange={(e) => updateQuoteSetting('business_address', e.target.value)} className="mt-1" rows={2} placeholder="123 Main St, Sydney NSW 2000" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={quoteSettings.business_phone} onChange={(e) => updateQuoteSetting('business_phone', e.target.value)} className="mt-1" placeholder="0412 345 678" />
                    </div>
                    <div>
                      <Label>Email (for quotes)</Label>
                      <Input value={quoteSettings.business_email} onChange={(e) => updateQuoteSetting('business_email', e.target.value)} className="mt-1" placeholder="quotes@yourbusiness.com.au" />
                      <p className="text-xs text-slate-500 mt-1">Falls back to your login email if blank</p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Pricing */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Pricing</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Default Hourly Rate (AUD)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-slate-500">$</span>
                        <Input type="number" min="0" step="0.01" value={quoteSettings.quote_hourly_rate} onChange={(e) => updateQuoteSetting('quote_hourly_rate', e.target.value)} placeholder="0.00" data-testid="qs-hourly-rate" />
                        <span className="text-sm text-slate-500 whitespace-nowrap">/ hour</span>
                      </div>
                    </div>
                    <div>
                      <Label>Quote Valid For</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="number" min="1" value={quoteSettings.quote_validity_days} onChange={(e) => updateQuoteSetting('quote_validity_days', e.target.value)} className="w-24" data-testid="qs-validity-days" />
                        <span className="text-sm text-slate-500">days</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* GST */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">GST</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2 flex items-center gap-3">
                      <Checkbox
                        checked={quoteSettings.gst_registered}
                        onCheckedChange={(val) => updateQuoteSetting('gst_registered', val)}
                        data-testid="qs-gst-registered"
                      />
                      <div>
                        <Label>Registered for GST</Label>
                        <p className="text-xs text-slate-500">Enable to show GST on quotes and PDFs</p>
                      </div>
                    </div>
                    {quoteSettings.gst_registered && (
                      <>
                        <div>
                          <Label>GST Rate</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input type="number" min="0" max="100" step="0.1" value={quoteSettings.gst_rate} onChange={(e) => updateQuoteSetting('gst_rate', e.target.value)} className="w-24" data-testid="qs-gst-rate" />
                            <span className="text-sm text-slate-500">%</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                          <Checkbox
                            checked={quoteSettings.gst_inclusive}
                            onCheckedChange={(val) => updateQuoteSetting('gst_inclusive', val)}
                            data-testid="qs-gst-inclusive"
                          />
                          <div>
                            <Label>Prices include GST</Label>
                            <p className="text-xs text-slate-500">If unchecked, GST is added on top of quoted prices</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Deposit */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Deposit</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label>Deposit Percentage</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="number" min="0" max="100" value={quoteSettings.deposit_percentage} onChange={(e) => updateQuoteSetting('deposit_percentage', e.target.value)} className="w-24" data-testid="qs-deposit-pct" />
                        <span className="text-sm text-slate-500">%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Set to 0 for no deposit requirement</p>
                    </div>
                    <div>
                      <Label>Deposit Note</Label>
                      <Input value={quoteSettings.deposit_note} onChange={(e) => updateQuoteSetting('deposit_note', e.target.value)} className="mt-1" placeholder="e.g. Deposit required before work commences" />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Payment & Terms */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Payment & Terms</h3>
                  <div className="grid gap-4">
                    <div>
                      <Label>Payment Instructions / Bank Details</Label>
                      <Textarea value={quoteSettings.payment_instructions} onChange={(e) => updateQuoteSetting('payment_instructions', e.target.value)} className="mt-1" rows={3} placeholder={"BSB: 062-000\nAccount: 1234 5678\nAccount Name: Your Trade Co."} data-testid="qs-payment-instructions" />
                      <p className="text-xs text-slate-500 mt-1">Included on every quote PDF</p>
                    </div>
                    <div>
                      <Label>Standard Terms & Conditions</Label>
                      <Textarea value={quoteSettings.standard_terms} onChange={(e) => updateQuoteSetting('standard_terms', e.target.value)} className="mt-1" rows={4} placeholder="e.g. All prices include GST. Work guaranteed for 12 months. Additional work outside this scope will be quoted separately." data-testid="qs-standard-terms" />
                      <p className="text-xs text-slate-500 mt-1">Auto-populated as the default Notes/Terms on new quotes</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveQuoteSettings} data-testid="save-quote-settings-btn">
                <Save className="mr-2 h-4 w-4" />
                Save Quote Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Label Dialog */}
      <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingLabel ? 'Edit Label' : 'Create Label'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Label Name</Label>
              <Input
                value={labelName}
                onChange={(e) => setLabelName(e.target.value)}
                placeholder="e.g., Bug, Feature, Urgent"
                className="mt-2"
                data-testid="label-name-input"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-transform ${labelColor === color ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setLabelColor(color)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Label>Custom:</Label>
                <Input
                  type="color"
                  value={labelColor}
                  onChange={(e) => setLabelColor(e.target.value)}
                  className="w-16 h-8 p-1 cursor-pointer"
                />
                <Input
                  value={labelColor}
                  onChange={(e) => setLabelColor(e.target.value)}
                  className="w-24"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Label>Preview:</Label>
              <Badge style={{ backgroundColor: labelColor + '20', color: labelColor, borderColor: labelColor }} className="border">
                {labelName || 'Label'}
              </Badge>
            </div>
            <div>
              <Label>AI Context (optional)</Label>
              <Textarea
                value={labelAiPrompt}
                onChange={(e) => setLabelAiPrompt(e.target.value)}
                placeholder="e.g., The SS Minnow is a wooden boat 30 foot long with a 3jhm5 yanmar engine..."
                rows={3}
                className="mt-2"
                data-testid="label-ai-prompt"
              />
              <p className="text-xs text-slate-500 mt-1">
                This context will be included in AI suggestions for tasks with this label.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLabelDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveLabel} data-testid="save-label-btn">
              {editingLabel ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
